import api from '../apiService';
import { redactSecretsInText } from './secretHeuristics';

/**
 * `web_search` — a query to OUR API, which forwards it to a self-hosted
 * SearXNG (22.08.2026).
 *
 * Why through our server and not a provider plugin: it works on every provider
 * path including BYO keys, results come back STRUCTURED ({ title, url,
 * snippet }) so the tool loop can hand a url to fetch_url / import_spec, and
 * the only thing that leaves the user's machine is the model-composed phrase.
 *
 * Contract (task PLAN, owner decisions): `POST /ai/search { q }` →
 * `{ results: [{ title, url, snippet }], engine_count }`; 503 when the server
 * has no search engine configured, 502 when SearXNG is unreachable, 429 with
 * `Retry-After` when a cap is hit, 401 when signed out.
 *
 * apiService's axios path is fine here: a search answers in a second or two,
 * nothing like the multi-minute model turn that must go through providerHttp
 * (tauriAxiosAdapter drops axios timeouts; the Rust 30 s default applies).
 */

/** The server's limit; clamped here so a long phrase degrades to a 200 rather than a 422. */
export const WEB_SEARCH_QUERY_MAX = 200;
/** Results shown and forwarded, whatever the server returns. */
export const WEB_SEARCH_MAX_RESULTS = 8;

const TITLE_MAX = 120;
const URL_MAX = 500;
const SNIPPET_MAX = 300;

export const WEB_SEARCH_MESSAGES = {
  unauthenticated: 'Sign in to use web search',
  not_configured: 'Web search is not available on this server',
  unavailable: 'Web search is temporarily unavailable',
  busy: (seconds) => (
    Number.isFinite(seconds) && seconds > 0
      ? `Web search is busy — try again in ${seconds} s`
      : 'Web search is busy — try again in a moment'
  ),
};

/** Strip tags, collapse whitespace, clip. */
function cleanText(value, max) {
  const text = String(value ?? '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return text.length > max ? text.slice(0, max) : text;
}

function readRetryAfter(headers) {
  if (!headers || typeof headers !== 'object') return null;
  const key = Object.keys(headers).find((k) => k.toLowerCase() === 'retry-after');
  if (!key) return null;
  const n = Number.parseInt(String(headers[key]), 10);
  return Number.isFinite(n) ? n : null;
}

/** Map an axios rejection to the one line the model (and the card) should see. */
export function describeSearchError(err) {
  const status = err?.response?.status;
  switch (status) {
    case 401: return WEB_SEARCH_MESSAGES.unauthenticated;
    case 429: return WEB_SEARCH_MESSAGES.busy(readRetryAfter(err.response?.headers));
    case 502: return WEB_SEARCH_MESSAGES.unavailable;
    case 503: return WEB_SEARCH_MESSAGES.not_configured;
    default: break;
  }
  if (status && err?.response?.data?.error) {
    return redactSecretsInText(String(err.response.data.error));
  }
  // A transport failure is the least curated string available; scrub it like
  // every other error that becomes prompt text.
  return redactSecretsInText(`Web search failed: ${err?.message || String(err)}`);
}

/** Normalise one server result; null when it is not a usable http(s) hit. */
function normalizeResult(r) {
  if (!r || typeof r !== 'object') return null;
  const url = String(r.url || '').trim();
  if (!/^https?:\/\//i.test(url) || url.length > URL_MAX) return null;
  return {
    title: cleanText(r.title, TITLE_MAX),
    url,
    snippet: cleanText(r.snippet ?? r.content, SNIPPET_MAX),
  };
}

/**
 * Run a search. Never throws: every failure is `{ error }`.
 *
 * @param {{ query: string }} input
 * @returns {Promise<{ query, result_count, engine_count, results } | { error }>}
 */
export async function webSearch({ query } = {}) {
  const q = String(query ?? '').replace(/\s+/g, ' ').trim().slice(0, WEB_SEARCH_QUERY_MAX);
  if (!q) return { error: 'query is required' };

  let data;
  try {
    data = await api.post('/ai/search', { q });
  } catch (err) {
    return { error: describeSearchError(err) };
  }

  const results = (Array.isArray(data?.results) ? data.results : [])
    .map(normalizeResult)
    .filter(Boolean)
    .slice(0, WEB_SEARCH_MAX_RESULTS);

  return {
    query: q,
    result_count: results.length,
    engine_count: Number.isFinite(data?.engine_count) ? data.engine_count : undefined,
    results,
  };
}

/**
 * What crosses back to the model: the query, counts and the results' three
 * fields. An explicit case, because the default projector drops `snippet` —
 * free text by its rules — and a search with no snippets is a list of links
 * the model cannot rank.
 */
export function condenseWebSearchResult(result) {
  if (!result || typeof result !== 'object') return result;
  return {
    query: result.query,
    result_count: result.result_count,
    engine_count: result.engine_count,
    results: (result.results || []).slice(0, WEB_SEARCH_MAX_RESULTS).map((r) => ({
      title: r.title,
      url: r.url,
      snippet: r.snippet,
    })),
  };
}
